import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Shield,
  HeartPulse,
  Scissors,
  Home as HomeIcon,
  Building2,
  Landmark,
  Users,
  Award,
  TreeDeciduous,
  UsersRound,
  ClipboardCheck,
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';
import ContactForm from '../components/ContactForm';
import BudgetForm from '../components/BudgetForm';
import FAQSection from '../components/FAQSection';
import TestimonialsSection from '../components/TestimonialsSection';
import ClientsSection from '../components/ClientsSection';
import MediaSection from '../components/MediaSection';
import NurserySection from '../components/NurserySection';
import ProjectsSection from '../components/ProjectsSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Memoized icon map to prevent recreation on each render
const iconMap = {
  'shield-check': Shield,
  'heart-pulse': HeartPulse,
  'scissors': Scissors,
  'home': HomeIcon,
  'building-2': Building2,
  'landmark': Landmark,
  'users': Users,
  'award': Award,
  'tree-deciduous': TreeDeciduous,
  'users-round': UsersRound,
  'clipboard-check': ClipboardCheck
};

// Static data that doesn't change
const clientTypes = [
  { id: 1, type: "Particulars", description: "Cura i manteniment de palmeres en jardins privats i residències.", icon: "home" },
  { id: 2, type: "Empreses", description: "Serveis integrals per a hotels, restaurants i empreses amb zones enjardinades.", icon: "building-2" },
  { id: 3, type: "Ajuntaments", description: "Col·laboració amb administracions públiques en la gestió de palmeres municipals.", icon: "landmark" },
  { id: 4, type: "Comunitats", description: "Manteniment de zones comunes i jardins de comunitats de veïns.", icon: "users" }
];

const Home = () => {
  const [activeForm, setActiveForm] = useState('contact');
  const [services, setServices] = useState([]);
  const [heroData, setHeroData] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const whyChooseUs = [
    { id: 1, title: "Més de 30 anys d'experiència", description: "Tres dècades dedicades al tractament i cura professional de palmeres a Catalunya.", icon: "award" },
    { id: 2, title: "Viver propi", description: "Disposem de viver privat a Sant Pol de Mar amb varietat d'espècies de qualitat.", icon: "tree-deciduous" },
    { id: 3, title: "Equip professional", description: "Tècnics especialitzats i equipament d'última generació per garantir els millors resultats.", icon: "users-round" },
    { id: 4, title: "Tractament integral", description: "Des de la prevenció fins a la cirurgia de palmeres, oferim solucions completes.", icon: "clipboard-check" }
  ];

  const processSteps = [
    { id: 1, title: "Inspecció inicial", description: "Avaluem l'estat de les vostres palmeres i detectem possibles problemes." },
    { id: 2, title: "Diagnòstic i pressupost", description: "Elaborem un informe detallat amb el millor tractament i pressupost personalitzat." },
    { id: 3, title: "Tractament professional", description: "Apliquem els tractaments necessaris amb tècniques provades i efectives." },
    { id: 4, title: "Seguiment i manteniment", description: "Realitzem seguiments periòdics per assegurar la salut de les vostres palmeres." }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [servicesRes, heroRes, companyRes] = await Promise.all([
        axios.get(`${API}/public/services`),
        axios.get(`${API}/public/hero`),
        axios.get(`${API}/public/company-info`)
      ]);
      setServices(servicesRes.data);
      setHeroData(heroRes.data);
      setCompanyInfo(companyRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      // Set default company info if API fails
      setCompanyInfo({
        name: "InfoPalmeras",
        tagline: "Especialistes en tractament i cura de palmeres",
        years_experience: 30,
        phone: "618 71 49 44",
        email: "info@infopalmeras.cat",
        address: "Viver de Sant Pol de Mar, Barcelona",
        twitter: "@InfoPalmerass",
        youtube: "https://www.youtube.com/channel/UCWoWOURwfPm0SyKyWtzn0HQ"
      });
    } finally {
      setLoading(false);
    }
  };

  const scrollToContact = () => {
    document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToBudget = () => {
    setActiveForm('budget');
    document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Show loading state
  if (loading || !companyInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  // Show maintenance mode page
  if (companyInfo.maintenance_mode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-50 to-green-100">
        <div className="text-center px-4 max-w-2xl">
          <div className="mb-8 bg-white rounded-2xl p-6 inline-block shadow-lg">
            <img 
              src="/logo.png" 
              alt="InfoPalmeras Logo" 
              className="h-24 w-auto object-contain mx-auto"
            />
          </div>
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 md:p-12 shadow-2xl">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4">
              Estem millorant la nostra web
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Estem realitzant tasques de manteniment per oferir-te una millor experiència. 
              Tornarem molt aviat!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href={`tel:${companyInfo.phone}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-semibold"
              >
                <Phone className="h-5 w-5" />
                Truca'ns: {companyInfo.phone}
              </a>
              <a 
                href={`mailto:${companyInfo.email}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-emerald-600 text-emerald-700 rounded-lg hover:bg-emerald-50 transition-colors font-semibold"
              >
                <Mail className="h-5 w-5" />
                {companyInfo.email}
              </a>
            </div>
          </div>
          <p className="mt-8 text-sm text-slate-500">
            {companyInfo.tagline}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-emerald-100 bg-white/95 backdrop-blur-md shadow-sm">
        <div className="container mx-auto px-4 h-24 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="/logo.png" 
              alt="InfoPalmeras Logo" 
              className="h-16 md:h-20 w-auto object-contain"
            />
          </div>
          <div className="flex items-center gap-4">
            <a href={`tel:${companyInfo.phone}`} className="hidden md:flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-emerald-600 transition-colors px-4 py-2 rounded-lg hover:bg-emerald-50">
              <Phone className="h-5 w-5" />
              {companyInfo.phone}
            </a>
            <Button onClick={scrollToBudget} size="sm" className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 shadow-lg shadow-emerald-500/30 font-semibold px-6">
              Sol·licitar pressupost
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-24 md:py-40 overflow-hidden bg-gradient-to-br from-emerald-50 via-teal-50 to-green-100">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMxMGI5ODEiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDEzNGg3djFoLTd2N2gtMXYtN2gtN3YtMWg3di03aDF2N3ptLTEgLTE0SDM2di0xaC03di03aC0xdjdoLTd2MWg3djdoMXYtN3oiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-40"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-5xl md:text-7xl font-extrabold text-slate-900 mb-6 leading-tight tracking-tight">
              {heroData ? heroData.title : 'Experts en tractament contra el Picudo Rojo'}
            </h2>
            <p className="text-xl md:text-2xl text-slate-700 mb-10 leading-relaxed font-light">
              {heroData ? heroData.description : 'Més de 30 anys d\'experiència en el tractament i cura de tot tipus d\'espècimens de palmera a Catalunya.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <Button onClick={scrollToBudget} size="lg" className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white shadow-lg shadow-emerald-500/30 text-lg px-10 py-6 font-semibold">
                Sol·licitar pressupost gratuït
                <ArrowRight className="ml-2 h-6 w-6" />
              </Button>
              <Button onClick={scrollToContact} variant="outline" size="lg" className="text-lg px-10 py-6 border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white font-semibold">
                Contactar-nos
              </Button>
            </div>
            <div className="mt-16 grid grid-cols-3 gap-8 max-w-3xl mx-auto">
              <div className="text-center bg-white/70 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
                <div className="text-4xl md:text-5xl font-extrabold bg-gradient-to-br from-emerald-600 to-green-600 bg-clip-text text-transparent mb-2">+{companyInfo.years_experience}</div>
                <div className="text-sm md:text-base text-slate-700 font-medium">Anys experiència</div>
              </div>
              <div className="text-center bg-white/70 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
                <div className="text-4xl md:text-5xl font-extrabold bg-gradient-to-br from-emerald-600 to-green-600 bg-clip-text text-transparent mb-2">100%</div>
                <div className="text-sm md:text-base text-slate-700 font-medium">Professionals</div>
              </div>
              <div className="text-center bg-white/70 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
                <div className="text-4xl md:text-5xl font-extrabold bg-gradient-to-br from-emerald-600 to-green-600 bg-clip-text text-transparent mb-2">24/7</div>
                <div className="text-sm md:text-base text-slate-700 font-medium">Disponibilitat</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Els nostres serveis
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Solucions completes per a la cura i protecció de les vostres palmeres
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 bg-white overflow-hidden group">
                  <CardHeader className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-xl font-bold text-slate-900">{service.title}</CardTitle>
                    <CardDescription className="text-slate-600 leading-relaxed">{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-sm text-slate-700">
                          <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMxMGI5ODEiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDEzNGg3djFoLTd2N2gtMXYtN2gtN3YtMWg3di03aDF2N3ptLTEgLTE0SDM2di0xaC03di03aC0xdjdoLTd2MWg3djdoMXYtN3oiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-50"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4">
              Per què triar <span className="bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">InfoPalmeras</span>?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              La confiança i experiència que necessites
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
            {whyChooseUs.map((item) => {
              const IconComponent = iconMap[item.icon];
              return (
                <div key={item.id} className="text-center group">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-green-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl group-hover:scale-110 transition-all duration-300">
                    <IconComponent className="h-10 w-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Com treballem
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Un procés simple i eficaç per cuidar les vostres palmeres
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {processSteps.map((step, index) => (
                <div key={step.id} className="relative">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-emerald-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                      {step.id}
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{step.title}</h3>
                    <p className="text-sm text-slate-600">{step.description}</p>
                  </div>
                  {index < processSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-6 left-[60%] w-[80%] h-0.5 bg-emerald-200"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Client Types */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              A qui servim
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Solucions professionals per a tot tipus de clients
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {clientTypes.map((client) => {
              const IconComponent = iconMap[client.icon];
              return (
                <Card key={client.id} className="text-center border-slate-200 hover:shadow-lg transition-all duration-300">
                  <CardHeader>
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="h-8 w-8 text-emerald-600" />
                    </div>
                    <CardTitle>{client.type}</CardTitle>
                    <CardDescription>{client.description}</CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Nursery Section - Viver de Palmeres */}
      <NurserySection />

      {/* Projects Section - Galeria de Treballs */}
      <ProjectsSection />

      {/* Media Section */}
      <MediaSection />

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* Clients Section */}
      <ClientsSection />

      {/* FAQ Section */}
      <FAQSection />

      {/* Contact Section */}
      <section id="contact-section" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Contacta amb nosaltres
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Estem aquí per ajudar-vos amb qualsevol consulta
            </p>
          </div>
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h3 className="text-2xl font-semibold text-slate-900 mb-6">Informació de contacte</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Telèfon</h4>
                    <a href={`tel:${companyInfo.phone}`} className="text-slate-600 hover:text-emerald-600 transition-colors">
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Email</h4>
                    <a href={`mailto:${companyInfo.email}`} className="text-slate-600 hover:text-emerald-600 transition-colors">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Viver</h4>
                    <p className="text-slate-600">{companyInfo.address}</p>
                  </div>
                </div>
              </div>
              <div className="mt-8 p-6 bg-emerald-50 rounded-lg border border-emerald-100">
                <h4 className="font-semibold text-slate-900 mb-2">Horari d'atenció</h4>
                {companyInfo.opening_hours ? (
                  companyInfo.opening_hours.split('\n').map((line, index) => (
                    <p key={index} className="text-slate-600 text-sm">{line}</p>
                  ))
                ) : (
                  <>
                    <p className="text-slate-600 text-sm">Dilluns a Divendres: 8:00 - 18:00</p>
                    <p className="text-slate-600 text-sm">Dissabte: 9:00 - 14:00</p>
                    <p className="text-slate-600 text-sm mt-2">Disponibilitat 24/7 per emergències</p>
                  </>
                )}
              </div>
            </div>

            {/* Forms */}
            <div>
              <div className="flex gap-2 mb-6">
                <Button
                  onClick={() => setActiveForm('contact')}
                  variant={activeForm === 'contact' ? 'default' : 'outline'}
                  className={activeForm === 'contact' ? 'bg-emerald-600 hover:bg-emerald-700' : 'border-emerald-600 text-emerald-700 hover:bg-emerald-50'}
                >
                  Formulari de contacte
                </Button>
                <Button
                  onClick={() => setActiveForm('budget')}
                  variant={activeForm === 'budget' ? 'default' : 'outline'}
                  className={activeForm === 'budget' ? 'bg-emerald-600 hover:bg-emerald-700' : 'border-emerald-600 text-emerald-700 hover:bg-emerald-50'}
                >
                  Sol·licitar pressupost
                </Button>
              </div>
              {activeForm === 'contact' ? <ContactForm /> : <BudgetForm />}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="mb-6 bg-white rounded-xl p-4 inline-block shadow-lg">
                <img 
                  src="/logo.png" 
                  alt="InfoPalmeras Logo" 
                  className="h-20 w-auto object-contain"
                />
              </div>
              <p className="text-slate-400 text-sm mt-4">
                {companyInfo.tagline}
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Serveis</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>Tractament Picudo Rojo</li>
                <li>Tractaments Preventius</li>
                <li>Venda de Palmeres</li>
                <li>Poda i Manteniment</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contacte</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>{companyInfo.phone}</li>
                <li>{companyInfo.email}</li>
                <li>{companyInfo.address}</li>
              </ul>
              <div className="mt-4">
                <h4 className="font-semibold mb-3">Segueix-nos</h4>
                <div className="flex gap-3">
                  <a 
                    href={companyInfo.youtube} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center transition-colors"
                    title="YouTube"
                  >
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  </a>
                  <a 
                    href={`https://twitter.com/${companyInfo.twitter.replace('@', '')}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-slate-700 hover:bg-slate-600 rounded-full flex items-center justify-center transition-colors"
                    title="Twitter"
                  >
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm text-slate-400">
            <p>&copy; {new Date().getFullYear()} {companyInfo.name}. Tots els drets reservats.</p>
            <p className="mt-2">
              Projecte by{' '}
              <a 
                href="https://digitalsunriselabs.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-bold text-emerald-400 hover:text-emerald-300 transition-colors"
              >
                Digital Sunrise Labs®
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
